import { createContext, useContext, useReducer } from "react";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { cookieOptions } from "../utils/cookieConfig";
import { hasValidToken } from "../utils/authSession";

const cookies = new Cookies();

function getInitialState() {
    const token = cookies.get("tokenRC");
    const user = cookies.get("userRC");
    if (hasValidToken(token) && user) {
        // Best-effort: restore Sentry user identity from cookie on page reload.
        // The JWT is not re-verified here — the backend validates it on every API call.
        Sentry.setUser({
            id: String(user.id),
            email: user.email || undefined,
        });
        return { contact: user, jwtToken: token };
    }
    return { contact: null, jwtToken: null };
}

const initialState = getInitialState();

export const ClientAuthContext = createContext(null);
export const ClientAuthDispatchContext = createContext(null);

export function ClientAuthProvider({ children }) {
    const [state, dispatch] = useReducer(clientAuthReducer, initialState);
    return (
        <ClientAuthContext.Provider value={state}>
            <ClientAuthDispatchContext.Provider value={dispatch}>
                {children}
            </ClientAuthDispatchContext.Provider>
        </ClientAuthContext.Provider>
    );
}

export function useClientAuth() {
    return useContext(ClientAuthContext);
}

export function useClientAuthDispatch() {
    return useContext(ClientAuthDispatchContext);
}

function clientAuthReducer(state, action) {
    switch (action.type) {
        case "login":
            cookies.set("userRCId", action.payload.contactId, cookieOptions);
            cookies.set("tokenRC", action.payload.token, cookieOptions);
            cookies.set("userRC", action.payload.contact, cookieOptions);
            // Set Sentry user context with email for error attribution
            Sentry.setUser({
                id: String(action.payload.contactId),
                email: action.payload.contact?.email || undefined,
            });
            return { contact: action.payload.contact, jwtToken: action.payload.token };
        case "logout":
            cookies.remove("userRCId", cookieOptions);
            cookies.remove("tokenRC", cookieOptions);
            cookies.remove("userRC", cookieOptions);
            // Clear Sentry user context on sign-out
            Sentry.setUser(null);
            return { contact: null, jwtToken: null };
        default:
            return state;
    }
}
